import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, colors } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';

import { Tournament } from '@aps-management/primapp-common';

const styles = (theme) => ({
  clickable: { cursor: 'pointer' },
  theader: {
    backgroundColor: colors.grey[50],
  },
  oddRow: {
    backgroundColor: '#f6f6f6',
  },
  flag: {
    width: 20,
    height: 12,
    border: `1px solid ${colors.grey[500]}`,
  },
  tableContainer: {
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    marginBottom: theme.spacing.unit * 4,
  },
  scoreContainer: {
    padding: '6px 12px',
  },
  none: {
    backgroundColor: colors.grey[300],
    borderRadius: 4,
  },
  par: {
    backgroundColor: colors.common.white,
    border: `1px solid ${colors.grey[500]}`,
    padding: '5px 11px',
    borderRadius: 4,
  },
  bogey: {
    backgroundColor: '#0094D6',
    color: colors.common.white,
    borderRadius: 4,
  },
  doubleBogey: {
    backgroundColor: '#003361',
    color: colors.common.white,
    borderRadius: 4,
  },
  tripleBogeyAndMore: {
    backgroundColor: '#A0A3A4',
    color: colors.common.white,
    borderRadius: 4,
  },
  eagleAndLess: {
    color: colors.common.white,
    backgroundColor: '#FACD00',
    borderRadius: 30,
  },
  birdie: {
    color: colors.common.white,
    backgroundColor: '#D20613CC',
    borderRadius: 30,
  },
  legend: {
    width: 20,
    height: 20,
    display: 'inline-block',
    verticalAlign: 'text-bottom',
    marginRight: theme.spacing.unit * 1,
  },
  legendItem: {
    display: 'inline-block',
    marginRight: theme.spacing.unit * 2,
  },
  legendPar: {
    backgroundColor: colors.common.white,
    border: `1px solid ${colors.grey[500]}`,
    borderRadius: 4,
  },
});

const Scorecard = ({ scorecard, numberOfRounds, classes }) => {
  const nbOfHoles = scorecard[0].par.length;
  const colSpan = numberOfRounds === 1 ? 5 : 5 + numberOfRounds;

  return (
    <TableRow>
      <TableCell colSpan={colSpan} style={{ padding: 16 }}>
        <Table padding='none'>
          <TableHead className={classes.theader}>
            <TableRow>
              <TableBoldCell>{'TROU'}</TableBoldCell>
              {[...Array(nbOfHoles)].map((_, h) => (
                <TableBoldCell key={h} align='center'>
                  {h + 1}
                </TableBoldCell>
              ))}
              <TableBoldCell align='right'>{'TOT'}</TableBoldCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {scorecard.map((sc, k) => (
              <React.Fragment key={k}>
                {k === 0 && (
                  <TableRow>
                    <TableCell>{'PAR'}</TableCell>
                    {[...Array(nbOfHoles)].map((_, h) => (
                      <ResultCell key={h} classes={classes} par={sc.par[h]} />
                    ))}
                    <TableBoldCell align='right'>{sc.totalPar}</TableBoldCell>
                  </TableRow>
                )}
                <TableRow key={`result-${k}`}>
                  <TableCell>{`T${k + 1}`}</TableCell>
                  {[...Array(nbOfHoles)].map((_, h) => (
                    <ResultCell key={h} classes={classes} par={sc.par[h]} score={sc.scores[h]} />
                  ))}
                  <TableBoldCell align='right'>
                    {sc.totalScore !== 0 && !sc.hasIncompleteHole ? sc.totalScore : '-'}
                  </TableBoldCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableCell>
    </TableRow>
  );
};

const TableBoldCell = withStyles({ root: { fontWeight: 800 } })(TableCell);
const ClickableRow = ({ children, onClick, isOdd, classes }) => (
  <TableRow onClick={onClick} className={classNames(classes.clickable, isOdd ? classes.oddRow : {})}>
    {children}
  </TableRow>
);

const ResultCell = ({ par, score = null, classes }) => {
  let className = null;
  let data = score == null ? par : score;

  if (score && score > 0) {
    const difference = score - par;

    if (difference < -1) {
      className = classes.eagleAndLess;
    } else if (difference === -1) {
      className = classes.birdie;
    } else if (difference === 1) {
      className = classes.bogey;
    } else if (difference === 2) {
      className = classes.doubleBogey;
    } else if (difference > 2) {
      className = classes.tripleBogeyAndMore;
    } else {
      className = classes.par;
    }
  } else if (score === 0) {
    data = 'X';
    className = classes.none;
  }

  return (
    <TableCell align='center'>
      <Typography inline variant='body2' component='span' className={classNames(classes.scoreContainer, className)}>
        {data}
      </Typography>
    </TableCell>
  );
};

const RankingTableRow = React.memo(
  ({ classes, rank, rankPosition, playerResult, showScorecard, toggleScorecard, showRanking, numberOfRounds }) => (
    <>
      <ClickableRow isOdd={rank % 2} classes={classes} onClick={() => toggleScorecard(`rank-${rank}-${rankPosition}`)}>
        <TableBoldCell>
          {!showRanking && playerResult.status === '00' && '-'}
          {rankPosition === 0 && playerResult.status !== '00' && playerResult.status}
          {showRanking && rankPosition === 0 && playerResult.status === '00' && rank + 1}
        </TableBoldCell>
        <TableCell padding='none'>
          {playerResult.nationality !== 'ZZZ' && (
            <img
              className={classes.flag}
              alt={playerResult.nationality}
              src={`https://web.ffgolf.org/resultats/img/flag/${playerResult.nationality}.png`}
            />
          )}
        </TableCell>
        <TableCell style={{ width: '100%' }}>
          {playerResult.lastName} {playerResult.firstName}
        </TableCell>
        <TableCell align='right'>{playerResult.hcp.toFixed(1)}</TableCell>
        {numberOfRounds > 1 &&
          [...Array(numberOfRounds)].map((_, h) => (
            <TableCell key={h} align='right'>
              {playerResult.scorecard[h].status === '00'
                ? playerResult.scorecard[h].result
                : playerResult.scorecard[h].status}
            </TableCell>
          ))}
        <TableBoldCell align='right'>
          {playerResult.status === '00' || playerResult.result > 0 ? playerResult.result : '-'}
        </TableBoldCell>
      </ClickableRow>
      {showScorecard && (
        <Scorecard classes={classes} numberOfRounds={numberOfRounds} scorecard={playerResult.scorecard} />
      )}
    </>
  ),
);

const RankingTableHeader = ({ numberOfRounds }) => (
  <TableHead>
    <TableRow>
      <TableBoldCell>#</TableBoldCell>
      <TableBoldCell padding='none' />
      <TableBoldCell />
      <TableBoldCell align='right'>Index</TableBoldCell>
      {numberOfRounds > 1 &&
        [...Array(numberOfRounds)].map((_, i) => <TableBoldCell key={i} align='right'>{`T${i + 1}`}</TableBoldCell>)}
      <TableBoldCell align='right'>Total</TableBoldCell>
    </TableRow>
  </TableHead>
);

const RankingTable = ({ classes, serie, resultType, numberOfRounds, results }) => {
  const [activeRows, setActiveRows] = useState(new Set());

  const uniqueRanks = new Set(results.map((r) => r.rank));
  const isRankingValid = uniqueRanks.size !== 1 || !uniqueRanks.has(0);

  const rankings = isRankingValid
    ? Tournament.functions.reorderResults(results)
    : Tournament.functions.reorderResultsWithoutRank(results);

  const toggleScorecard = useCallback((playerKey) => {
    setActiveRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(playerKey)) {
        newSet.delete(playerKey);
      } else {
        newSet.add(playerKey);
      }

      return newSet;
    });
  }, []);

  return (
    <>
      {serie && (
        <Typography gutterBottom variant='body1' align='center'>
          {serie.name} - {serie.gameFormula} - {resultType}
        </Typography>
      )}
      <Paper className={classes.tableContainer}>
        <Table padding='dense'>
          <RankingTableHeader classes={classes} numberOfRounds={numberOfRounds} />
          <TableBody>
            {rankings.map((rank, r) =>
              rank.map((playerResult, p) => {
                const key = `rank-${r}-${p}`;

                return (
                  <RankingTableRow
                    key={key}
                    rank={r}
                    rankPosition={p}
                    playerResult={playerResult}
                    showRanking={isRankingValid}
                    numberOfRounds={numberOfRounds}
                    toggleScorecard={toggleScorecard}
                    showScorecard={activeRows.has(key)}
                    classes={classes}
                  />
                );
              }),
            )}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};

const scoreLegendMap = [
  { label: 'Eagle et moins', className: 'eagleAndLess' },
  { label: 'Birdie', className: 'birdie' },
  { label: 'Par', className: 'legendPar' },
  { label: 'Bogey', className: 'bogey' },
  { label: 'Double Bogey', className: 'doubleBogey' },
  { label: 'Triple Bogey et plus', className: 'tripleBogeyAndMore' },
];

const RankingTableLegend = withStyles(styles)(({ classes }) => (
  <Box marginBottom={4}>
    {scoreLegendMap.map((rank) => (
      <Typography key={rank.label} variant='caption' className={classes.legendItem}>
        <span className={classNames(classes.legend, classes[rank.className])}></span>
        {rank.label}
      </Typography>
    ))}
  </Box>
));

export { RankingTableLegend };
export default withStyles(styles)(RankingTable);
